import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Button, Divider, Input, Select, Tooltip } from 'antd';

import { useTranslation } from 'react-i18next';
import { dateFormat, getDateInISOFormat } from '@utils/formatters';
import Table from '@components/Table';
import useSwr from '@hooks/useSwr';
import 'moment/locale/pt-br';
import moment from 'moment';
import { FiExternalLink } from 'react-icons/fi';
import { IObject } from '@utils/defaultTypes';
import { useNavigate } from 'react-router-dom';
import useSearchParams from '@hooks/useSearchParams';
import StatusMenu from '@components/StatusMenu';
import QuickRangePicker from '@components/DatePicker/QuickRangePicker';
import RequestReport from '@components/RequestReport';
import { ApplicationsConstants } from '@utils/applicationsConstants';
import rolesConstants from '@utils/rolesConstants';
import CustomTag from '@components/CustomTag';
import EventsListDrawer from './EventsListDrawer';

const { Search } = Input;
interface IPagination {
  page?: number | string;
  linesPerPage?: number | string;
}
interface ITransfers {
  defaultFilters?: IObject;
  defaultPagination?: IPagination;
  setDefaultPaginationChange?: (pagination: IPagination) => void;
}

const TransactionsAcquiring: React.FC<ITransfers> = ({
  defaultFilters,
  defaultPagination,
  setDefaultPaginationChange,
}) => {
  const { t } = useTranslation(['transfers', 'invoices']);
  const navigate = useNavigate();
  const [merchantNumberValue, setMerchantNumberValue] = useState('');
  const [deviceNumberValue, setDeviceNumberValue] = useState('');
  const [intervalDatesToSearch, setIntervalDatesToSearch] = useState('today');

  const {
    setSearchParams,
    hasValuesInExtraParams,
    clearSearchParams,
    searchParams,
  } = useSearchParams({
    type: 'pagination',
    extraParams: [
      'merchantNumber',
      'status',
      'deviceNumber',
      'startDate',
      'endDate',
    ],
  });

  const {
    merchantNumber,
    deviceNumber,
    startDate,
    endDate,
    status,
    page,
    linesPerPage,
  } = useMemo(() => searchParams, [searchParams]);

  const filters = useMemo(() => {
    const allFilters = {
      page: page ? Number(page) - 1 : 0,
      linesPerPage: linesPerPage ?? 20,
      deviceNumber,
      merchantNumber,
      status,
      startDate: startDate
        ? moment(startDate).format('DD/MM/YYYY HH:mm')
        : moment().startOf('day').format('DD/MM/YYYY HH:mm'),
      endDate: endDate
        ? moment(endDate).format('DD/MM/YYYY HH:mm')
        : moment().endOf('day').format('DD/MM/YYYY HH:mm'),
      ...defaultFilters,
    };

    return allFilters;
  }, [
    page,
    linesPerPage,
    merchantNumber,
    startDate,
    endDate,
    status,
    deviceNumber,
    defaultFilters,
  ]);

  const { data } = useSwr('/service-proxy/transactionTpdu', {
    params: filters,
  });

  useEffect(() => {
    setMerchantNumberValue(merchantNumber);
  }, [merchantNumber]);

  useEffect(() => {
    setDeviceNumberValue(deviceNumber);
  }, [deviceNumber]);

  const columns = useMemo(() => {
    const allColumns = [
      {
        title: t('acquiring.table.applicationName'),
        align: 'center' as const,
        dataIndex: 'applicationName',
      },
      {
        title: t('acquiring.table.applicationType'),
        align: 'center' as const,
        dataIndex: 'applicationType',
      },
      {
        title: t('acquiring.table.auditNumber'),
        align: 'center' as const,
        dataIndex: 'auditNumber',
      },
      {
        title: t('acquiring.table.destinationServerHost'),
        align: 'center' as const,
        dataIndex: 'destinationServerHost',
      },
      {
        title: t('acquiring.table.destinationServerPort'),
        align: 'center' as const,
        dataIndex: 'destinationServerPort',
      },
      {
        title: t('acquiring.table.deviceNumber'),
        align: 'center' as const,
        dataIndex: 'deviceNumber',
      },
      {
        title: t('acquiring.table.idTransaction'),
        align: 'center' as const,
        dataIndex: 'idTransaction',
      },
      {
        title: t('acquiring.table.merchantNumber'),
        align: 'center' as const,
        dataIndex: 'merchantNumber',
      },
      {
        title: t('acquiring.table.serverHost'),
        align: 'center' as const,
        dataIndex: 'serverHost',
      },
      {
        title: t('acquiring.table.serverPort'),
        align: 'center' as const,
        dataIndex: 'serverPort',
      },
      {
        title: t('acquiring.table.duration'),
        align: 'center' as const,
        dataIndex: 'duration',
        render: (value: number) => `${value} ms`,
      },
      {
        title: t('acquiring.table.site'),
        align: 'center' as const,
        dataIndex: 'site',
      },
      {
        title: t('acquiring.table.date'),
        align: 'center' as const,
        dataIndex: 'startDate',
        render: dateFormat,
      },
      {
        title: t('acquiring.table.status'),
        dataIndex: 'status',
        render: (value?: string) => (
          <CustomTag color={value === 'SUCCESS' ? 'green' : 'red'}>
            {t(`table.result.options.${value}`)}
          </CustomTag>
        ),
      },
      {
        title: t('acquiring.table.totalBytes'),
        align: 'center' as const,
        dataIndex: 'totalBytes',
      },
      {
        title: t('acquiring.table.eventList'),
        align: 'center' as const,
        dataIndex: 'actions',
        render: (_: any, record: any) => (
          <EventsListDrawer transaction={record} />
        ),
      },
    ].filter(col => col);

    return allColumns;
  }, [t]);

  const handleDateChange = useCallback(
    (values: any) => {
      const start = getDateInISOFormat(values.startDate);
      const end = getDateInISOFormat(values.endDate);
      setSearchParams({ startDate: start, endDate: end });
    },
    [setSearchParams],
  );

  const tableFilters = useMemo(
    () => [
      {
        visible: true,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('acquiring.table.date'),
        element: (
          <QuickRangePicker
            onChange={handleDateChange}
            value={
              startDate
                ? [moment(startDate, 'YYYYMMDD'), moment(endDate, 'YYYYMMDD')]
                : undefined
            }
            defaultValue={
              startDate
                ? [moment(startDate, 'YYYYMMDD'), moment(endDate, 'YYYYMMDD')]
                : [moment(), moment()]
            }
            style={{ width: '100%' }}
          />
        ),
      },

      {
        visible: true,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('acquiring.table.deviceNumber'),
        element: (
          <Search
            onSearch={value => setSearchParams({ deviceNumber: value })}
            onChange={({ target: { value } }) => setDeviceNumberValue(value)}
            value={deviceNumberValue}
            allowClear
          />
        ),
      },

      {
        visible: true,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('acquiring.table.merchantNumber'),
        element: (
          <Search
            onSearch={value => setSearchParams({ merchantNumber: value })}
            onChange={({ target: { value } }) => setMerchantNumberValue(value)}
            value={merchantNumberValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 12,
          xl: 8,
          xs: 24,
        },
        label: t('acquiring.table.status'),
        element: (
          <Select
            optionFilterProp="label"
            options={[
              { label: t('table.result.options.SUCCESS'), value: 'SUCCESS' },
              { label: t('table.result.options.ERROR'), value: 'ERROR' },
              {
                label: t('table.result.options.SYSTEMERROR'),
                value: 'SYSTEMERROR',
              },
              {
                label: t('table.result.options.REQUESTPARSEERROR'),
                value: 'REQUESTPARSEERROR',
              },
              {
                label: t('table.result.options.POSWRITEERROR'),
                value: 'POSWRITEERROR',
              },
              {
                label: t('table.result.options.MAINFRAMECONNERROR'),
                value: 'MAINFRAMECONNERROR',
              },
              {
                label: t('table.result.options.MFTIMEOUT'),
                value: 'MFTIMEOUT',
              },
              {
                label: t('table.result.options.EMPTYRESPONSE'),
                value: 'EMPTYRESPONSE',
              },
              {
                label: t('table.result.options.BYPASS'),
                value: 'BYPASS',
              },
            ]}
            style={{ width: '100%' }}
            // @ts-ignore
            value={status?.toUpperCase()}
            allowClear
            showSearch
            onChange={value => {
              setSearchParams({ status: value });
            }}
          />
        ),
      },
    ],
    [
      t,
      handleDateChange,
      startDate,
      merchantNumberValue,
      deviceNumberValue,
      setSearchParams,
      endDate,
      status,
    ],
  );

  const allMenuFilter = [
    {
      label: t('invoices:today'),
      value: 'today',
      color: 'var(--primary-color)',
    },
    {
      label: t('invoices:three-months-ago'),
      value: 'three',
      color: 'var(--primary-color)',
    },
    {
      label: t('invoices:six-months-ago'),
      value: 'six',
      color: 'var(--primary-color)',
    },
    {
      label: t('invoices:nine-months-ago'),
      value: 'nine',
      color: 'var(--primary-color)',
    },
    {
      label: t('invoices:one-year-ago'),
      value: 'one',
      color: 'var(--primary-color)',
    },
  ];

  const changeIntervalDates = (interval: string) => {
    setIntervalDatesToSearch(interval);
    switch (interval) {
      case 'one':
        setSearchParams({
          startDate: getDateInISOFormat(
            moment().subtract(12, 'months').toISOString(),
          ),
          endDate: getDateInISOFormat(moment().endOf('day').toISOString()),
        });
        break;
      case 'three':
        setSearchParams({
          startDate: getDateInISOFormat(
            moment().subtract(3, 'months').toISOString(),
          ),
          endDate: getDateInISOFormat(moment().endOf('day').toISOString()),
        });
        break;
      case 'six':
        setSearchParams({
          startDate: getDateInISOFormat(
            moment().subtract(6, 'months').toISOString(),
          ),
          endDate: getDateInISOFormat(moment().endOf('day').toISOString()),
        });
        break;
      case 'nine':
        setSearchParams({
          startDate: getDateInISOFormat(
            moment().subtract(9, 'months').toISOString(),
          ),
          endDate: getDateInISOFormat(moment().endOf('day').toISOString()),
        });
        break;
      case 'today':
        setSearchParams({
          startDate: getDateInISOFormat(moment().startOf('day').toISOString()),
          endDate: getDateInISOFormat(moment().endOf('day').toISOString()),
        });
        break;
      default:
        break;
    }
  };

  return (
    <div>
      <StatusMenu
        itemActive={intervalDatesToSearch}
        data={allMenuFilter}
        handleSelectedCard={item => {
          changeIntervalDates(item.value);
        }}
        style={{ marginBottom: 16 }}
      />
      <Table
        // @ts-ignore
        columns={columns}
        data={data?.content}
        loading={!data?.content}
        mainExtraAction={
          <>
            {defaultFilters && (
              <>
                <Divider type="vertical" style={{ marginLeft: 16 }} />
                <Tooltip title={t('acquiring.viewOnPage')}>
                  <Button
                    icon={
                      <FiExternalLink
                        style={{
                          marginTop: 4,
                        }}
                      />
                    }
                    size="large"
                    type="link"
                    onClick={() => {
                      navigate(
                        `/${ApplicationsConstants.MOT_PAY}/assets/transactions-acquiring?deviceNumber=${defaultFilters?.deviceNumber}`,
                      );
                    }}
                  />
                </Tooltip>
              </>
            )}
          </>
        }
        tableKey="transferAcquiriring"
        title={t('acquiring.title')}
        filters={{
          list: tableFilters,
          hasValuesInFilters: hasValuesInExtraParams,
          onClearFilters: () => clearSearchParams(),
        }}
        extraActions={
          <RequestReport
            items={[
              {
                role: rolesConstants.REQUEST_REPORT,
                params: filters,
                path: `/service-proxy/transactionTpdu/report`,
                type: `TPDU_TRANSACTION`,
                method: 'get',
                disabled: !filters.deviceNumber,
                errorMessage: t(
                  'invoices:report.msgs.error-not-selected-logic',
                ),
              },
            ]}
          />
        }
        onTableChange={setDefaultPaginationChange ?? setSearchParams}
        pagination={{
          page,
          linesPerPage,
          total: data?.totalElements,
          ...defaultPagination,
        }}
      />
    </div>
  );
};

export default TransactionsAcquiring;
